<template>
  <div class="when-can-you-choose-form">
    <h4 class="global-h4 when-can-you-choose-form__title">When can you choose a creator?</h4>

    <div class="when-can-you-choose-form__text">
      <p class="global-p">Creators have {{ DaysCreatorsHaveToApply }} days to apply.</p>
      <p class="global-p">Please choose a creator as soon as possible after that (within ~{{ DaysToChooseCreator }} days from posting)</p>
    </div>

    <input
      class="global-text-input when-can-you-choose-form__date"
      type="date"
      v-model="_chooseCreatorEstimatedDate"
      :min="moment().format('YYYY-MM-DD')"
      @change="
        () => {
          if (!isLateDateWarningVisible) {
            _chooseCreatorEstimatedNotes = null;
          }
        }
      "
    />

    <template v-if="isLateDateWarningVisible">
      <NoteWithIcon class="when-can-you-choose-form__warning" size="small">
        <template #text> Choosing a creator earlier helps lock in their availability and keeps your creator visit on track. </template>
      </NoteWithIcon>

      <textarea class="global-textarea when-can-you-choose-form__estim-note" placeholder="Tell us why you need additional time. (optional)" v-model="_chooseCreatorEstimatedNotes"></textarea>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import moment from "moment";
import { DaysCreatorsHaveToApply, DaysToChooseCreator, ChooseCreatorRecommendedDaysLimit, ChooseCreatorDaysLimitWithoutWarning } from "@constants/collab";

const props = withDefaults(
  defineProps<{
    chooseCreatorEstimatedDate: string | null;
    chooseCreatorEstimatedNotes: string | null;
  }>(),
  {
    chooseCreatorEstimatedDate: null,
    chooseCreatorEstimatedNotes: null,
  }
);

const emit = defineEmits<{
  (e: "update:chooseCreatorEstimatedDate", value: string): void;
  (e: "update:chooseCreatorEstimatedNotes", value: string): void;
}>();

// Components
import NoteWithIcon from "@components/NoteWithIcon.vue";

// Local values ===============================================================
const _chooseCreatorEstimatedDate = ref<string | null>(null);
const _chooseCreatorEstimatedNotes = ref<string | null>(null);

onMounted(() => {
  if (!props.chooseCreatorEstimatedDate) {
    _chooseCreatorEstimatedDate.value = moment().add(ChooseCreatorRecommendedDaysLimit, "days").format("YYYY-MM-DD");
  } else {
    _chooseCreatorEstimatedDate.value = moment(props.chooseCreatorEstimatedDate).format("YYYY-MM-DD");
  }

  _chooseCreatorEstimatedNotes.value = props.chooseCreatorEstimatedNotes;
});

watch(_chooseCreatorEstimatedDate, () => {
  emit("update:chooseCreatorEstimatedDate", _chooseCreatorEstimatedDate.value);
});
watch(_chooseCreatorEstimatedNotes, () => {
  emit("update:chooseCreatorEstimatedNotes", _chooseCreatorEstimatedNotes.value);
});

// Late date warning ==========================================================
const isLateDateWarningVisible = computed<boolean>(() => {
  const selectedDate = moment(_chooseCreatorEstimatedDate.value);
  const daysLimitDate = moment().add(ChooseCreatorDaysLimitWithoutWarning, "days");

  return selectedDate.isAfter(daysLimitDate, "day");
});
</script>

<style scoped lang="scss">
// "When can you choose?" form ================================================
.when-can-you-choose-form {
  &__title {
    margin-bottom: 7px;
  }

  &__text {
    margin-bottom: 15px;
  }

  &__date {
    max-width: 150px;
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__warning {
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__estim-note {
    height: 90px;
    min-height: 90px;
  }
}
</style>
